import { ShopifyListingStatus } from "@redotech/cockroachdb-codegen/tables/product/shopify-listing-status";
import {
  shopifyListingIdFormat,
  shopifyPublicationIdFormat,
} from "@redotech/redo-marketplace-id";
import { ShopifyProductFieldType } from "@redotech/redo-prisma-client/enums";
import { attributeDefinitionIdFormat } from "@redotech/redo-product-id";
import { ShopifyIdType } from "@redotech/shopify-util/id";
import { shopifyGidSchema } from "@redotech/shopify-util/id-schema";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  AttributeType,
  AttributeValueSchema,
  AttributeValueType,
} from "../attributes";
import { BaseProductFamilyListingSchema, ListingType } from "../base-listing";
import { RedoListingStatus } from "../redo-listing-status";
import { ShopifyProductCategorySchema } from "./common-schemas";
import {
  ShopifyListingMediaSchema,
  ShopifyListingMediaWithoutRedoIds,
  ShopifyListingMediaWithShopifyIds,
} from "./shopify-media";
import { ShopifyMetafieldSchema } from "./shopify-metafields";
import {
  ShopifyListingPublicationSchema,
  ShopifyListingPublicationWithoutRedoIds,
  ShopifyListingPublicationWithShopifyIds,
} from "./shopify-publication";
import {
  ShopifyVariantListingSchema,
  ShopifyVariantListingWithoutRedoIds,
  ShopifyVariantListingWithShopifyIds,
  ShopifyVariantMappingSchema,
} from "./shopify-variant";

export const ShopifyProductStatusSchema = createEnum(ShopifyListingStatus);

export const ShopifyListingIdWithStatusSchema = z.object({
  id: shopifyListingIdFormat.schema,
  status: ShopifyProductStatusSchema,
  selectedPublicationIds: z
    .array(shopifyPublicationIdFormat.schema)
    .readonly()
    .optional(),
});
export type ShopifyListingIdWithStatus = z.infer<
  typeof ShopifyListingIdWithStatusSchema
>;

export function convertShopifyListingStatusToRedoListingStatus(
  status: ShopifyListingStatus,
): RedoListingStatus {
  switch (status) {
    case ShopifyListingStatus.Active:
      return RedoListingStatus.ACTIVE;
    case ShopifyListingStatus.Draft:
      return RedoListingStatus.DRAFT;
    case ShopifyListingStatus.Archived:
      return RedoListingStatus.ARCHIVED;
    case ShopifyListingStatus.Unlisted:
      return RedoListingStatus.INACTIVE;
    default:
      assertNever(status);
  }
}

export const ShopifyProductFieldTypeSchema = createEnum(
  ShopifyProductFieldType,
);

const ShopifyListingOptionSchema_ = z.object({
  name: z.string(),
  position: z.number(),
  values: z.array(z.string()),
});
export const ShopifyListingOptionSchema: z.ZodType<ShopifyListingOption> =
  ShopifyListingOptionSchema_;
export interface ShopifyListingOption extends z.infer<
  typeof ShopifyListingOptionSchema_
> {}

const ShopifySeoSchema_ = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
});
export const ShopifySeoSchema: z.ZodType<ShopifySeo> = ShopifySeoSchema_;
export interface ShopifySeo extends z.infer<typeof ShopifySeoSchema_> {}

export const ShopifyListingSchema_ = BaseProductFamilyListingSchema.extend({
  listingType: z.literal(ListingType.Shopify),
  id: z.bigint(),
  shopifyId: shopifyGidSchema(ShopifyIdType.Product).nullable(),
  title: z.string(),
  description: z.string(),
  handle: z.string(),
  productType: z.string(),
  category: ShopifyProductCategorySchema.nullable(),
  vendor: z.string(),
  tags: z.array(z.string()),
  options: z.array(ShopifyListingOptionSchema),
  seo: ShopifySeoSchema,
  status: ShopifyProductStatusSchema,
  metafields: z.array(ShopifyMetafieldSchema),
  variants: z.array(ShopifyVariantListingSchema),
  media: z.array(ShopifyListingMediaSchema),
  publications: z.array(ShopifyListingPublicationSchema),
  // collections: z.array(ShopifyCollectionSchema), // We aren't using collections yet.
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const ShopifyListingSchema: z.ZodType<ShopifyListing> =
  ShopifyListingSchema_;
export interface ShopifyListing extends z.infer<typeof ShopifyListingSchema_> {}

export type ShopifyListingWithoutRedoIds = Omit<
  ShopifyListing,
  "id" | "variants" | "media" | "publications" | "productFamilyId" // | "collections" // We aren't using collections yet.
> & {
  variants: ShopifyVariantListingWithoutRedoIds[];
  media: ShopifyListingMediaWithoutRedoIds[];
  publications: ShopifyListingPublicationWithoutRedoIds[];
  // collections: ShopifyCollectionWithoutRedoIds[]; // We aren't using collections yet.
};

export type ShopifyListingWithShopifyIds = Omit<
  ShopifyListing,
  "id" | "variants" | "media" | "publications" // | "collections" // We aren't using collections yet.
> & {
  shopifyId: string;
  variants: ShopifyVariantListingWithShopifyIds[];
  media: ShopifyListingMediaWithShopifyIds[];
  publications: ShopifyListingPublicationWithShopifyIds[];
  // collections: ShopifyCollectionWithShopifyIds[]; // We aren't using collections yet.
};

export const ShopifyProductAttributeType = {
  [ShopifyProductFieldType.category_id]: AttributeType.ProductCategory,
  [ShopifyProductFieldType.title]: AttributeType.ShortText,
  [ShopifyProductFieldType.description]: AttributeType.RichText,
  [ShopifyProductFieldType.vendor]: AttributeType.ShortText,
  [ShopifyProductFieldType.product_type]: AttributeType.ShortText,
  [ShopifyProductFieldType.handle]: AttributeType.ShortText,
  [ShopifyProductFieldType.seo_title]: AttributeType.ShortText,
  [ShopifyProductFieldType.seo_description]: AttributeType.ShortText,
  [ShopifyProductFieldType.metafield]: AttributeType.Json,
} satisfies Record<ShopifyProductFieldType, AttributeType>;

const ShopifyProductMappingSchema_ = z.object({
  fieldName: ShopifyProductFieldTypeSchema,
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const ShopifyProductMappingSchema: z.ZodType<ShopifyProductMapping> =
  ShopifyProductMappingSchema_;
export interface ShopifyProductMapping extends z.infer<
  typeof ShopifyProductMappingSchema_
> {}

export const ShopifyMappingSchema_ = z.object({
  productMappings: z.array(ShopifyProductMappingSchema).readonly(),
  variantMappings: z.array(ShopifyVariantMappingSchema).readonly(),
});
export const ShopifyMappingSchema: z.ZodType<ShopifyMapping> =
  ShopifyMappingSchema_;
export interface ShopifyMapping extends z.infer<typeof ShopifyMappingSchema_> {}

export const ShopifyProductAttributeSchema = {
  [ShopifyProductFieldType.category_id]:
    AttributeValueSchema[
      ShopifyProductAttributeType[ShopifyProductFieldType.category_id]
    ].nullable(),
  [ShopifyProductFieldType.title]:
    AttributeValueSchema[
      ShopifyProductAttributeType[ShopifyProductFieldType.title]
    ].nullable(),
  [ShopifyProductFieldType.description]:
    AttributeValueSchema[
      ShopifyProductAttributeType[ShopifyProductFieldType.description]
    ].nullable(),
  [ShopifyProductFieldType.vendor]:
    AttributeValueSchema[
      ShopifyProductAttributeType[ShopifyProductFieldType.vendor]
    ].nullable(),
  [ShopifyProductFieldType.product_type]:
    AttributeValueSchema[
      ShopifyProductAttributeType[ShopifyProductFieldType.product_type]
    ].nullable(),
  [ShopifyProductFieldType.handle]:
    AttributeValueSchema[
      ShopifyProductAttributeType[ShopifyProductFieldType.handle]
    ].nullable(),
  [ShopifyProductFieldType.seo_title]:
    AttributeValueSchema[
      ShopifyProductAttributeType[ShopifyProductFieldType.seo_title]
    ].nullable(),
  [ShopifyProductFieldType.seo_description]:
    AttributeValueSchema[
      ShopifyProductAttributeType[ShopifyProductFieldType.seo_description]
    ].nullable(),
  [ShopifyProductFieldType.metafield]:
    AttributeValueSchema[
      ShopifyProductAttributeType[ShopifyProductFieldType.metafield]
    ].nullable(),
};

export type ShopifyProductAttributeValueType = {
  [key in ShopifyProductFieldType]: (typeof ShopifyProductAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getShopifyProductAttributeValue<
  T extends ShopifyProductFieldType,
>(field: T, value: unknown): ShopifyProductAttributeValueType[T] | null {
  return ShopifyProductAttributeSchema[field].parse(
    value,
  ) as ShopifyProductAttributeValueType[T];
}

export function getShopifyProductAttributeType<
  T extends ShopifyProductFieldType,
>(field: T): (typeof ShopifyProductAttributeType)[T] {
  return ShopifyProductAttributeType[field];
}

export function getShopifyListingAttributeValue<
  T extends ShopifyProductFieldType,
>({
  listing,
  field,
}: {
  listing: ShopifyListing;
  field: T;
}): AttributeValueType[AttributeType] | null {
  let fieldValue;
  switch (field) {
    case "title":
      fieldValue = listing.title;
      break;
    case "description":
      fieldValue = listing.description;
      break;
    case "handle":
      fieldValue = listing.handle;
      break;
    case "product_type":
      fieldValue = listing.productType;
      break;
    case "seo_title":
      fieldValue = listing.seo?.title ?? null;
      break;
    case "seo_description":
      fieldValue = listing.seo?.description ?? null;
      break;
    case "vendor":
      fieldValue = listing.vendor;
      break;
    case "category_id":
      fieldValue = listing.category;
      break;
    case "metafield":
      fieldValue = null;
      break;
    default:
      assertNever(field);
  }

  return ShopifyProductAttributeSchema[field].parse(fieldValue);
}
